// Sentry API configuration
const SENTRY_CONFIG = {
  token: '6ef2b2d35cdf06f4eb377ddd7a2940ad7fd97b5ffd9f37aefe1298eeeb3c8402',
  organization: 'ailmsco',
  project: '4509688899436624', //'thrive360',
  baseUrl: 'https://sentry.io/api/0'
};

class SentryService {
  constructor() {
    this.config = SENTRY_CONFIG;
    this.isAuthenticated = false;
  }

  // Initialize Sentry API
  async initialize() {
    try {
      // Test the API connection by fetching project stats
      const stats = await this.getProjectStats();
      this.isAuthenticated = stats.totalEvents >= 0; // If we get a response, we're authenticated
      console.log('Sentry service initialized');
      return this.isAuthenticated;
    } catch (error) {
      console.error('Failed to initialize Sentry:', error);
      this.isAuthenticated = false;
      return false;
    }
  }

  // Make authenticated request to Sentry API
  async makeRequest(endpoint) {
    try {
      const response = await fetch(`${this.config.baseUrl}${endpoint}`, {
        headers: {
          'Authorization': `Bearer ${this.config.token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Sentry API error: ${response.status} ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Sentry API request failed:', error);
      throw error;
    }
  }

  // Get project statistics
  async getProjectStats() {
    try {
      // Correct endpoint for project stats
      const endpoint = `/projects/${this.config.organization}/${this.config.project}/stats/`;
      const stats = await this.makeRequest(endpoint);
      
      // Calculate project statistics from the stats data
      const totalEvents = stats.reduce((sum, stat) => sum + stat[1], 0);
      
      // Get issues count
      const issues = await this.getIssues(1000); // Get all issues for counting
      const resolvedIssues = issues.filter(issue => issue.status === 'resolved').length;
      const unresolvedIssues = issues.filter(issue => issue.status === 'unresolved').length;
      
      return {
        totalEvents,
        totalIssues: issues.length,
        resolvedIssues,
        unresolvedIssues
      };
    } catch (error) {
      console.error('Failed to fetch project stats:', error);
      return {
        totalEvents: 0,
        totalIssues: 0,
        resolvedIssues: 0,
        unresolvedIssues: 0
      };
    }
  }

  // Get issues list
  async getIssues(limit = 50, status = 'all') {
    try {
      let endpoint = `/organizations/${this.config.organization}/issues/?project=${this.config.project}&limit=${limit}`;
      
      if (status !== 'all') {
        endpoint += `&query=is:${status}`;
      }

      const issues = await this.makeRequest(endpoint);
      
      return issues.map(issue => ({
        id: issue.id,
        title: issue.title,
        level: issue.level,
        status: issue.status,
        count: issue.count,
        userCount: issue.userCount,
        firstSeen: issue.firstSeen,
        lastSeen: issue.lastSeen,
        permalink: issue.permalink,
        metadata: issue.metadata
      }));
    } catch (error) {
      console.error('Failed to fetch issues:', error);
      return [];
    }
  }

  // Get events/logs
  async getEvents(limit = 50, level = 'all') {
    try {
      let endpoint = `/organizations/${this.config.organization}/events/?project=${this.config.project}&limit=${limit}&field=title`;
      
      if (level !== 'all') {
        endpoint += `&query=level:${level}`;
      }

      const eventsResponse = await this.makeRequest(endpoint);
      const events = eventsResponse.data || []; // fallback to empty array if undefined

      return events.map(event => ({
        id: event.id,
        message: event.message,
        level: event.level,
        timestamp: event.timestamp,
        user: event.user,
        tags: event.tags,
        context: event.context
      }));
    } catch (error) {
      console.error('Failed to fetch events:', error);
      return [];
    }
  }

  // Get issue details
  async getIssueDetails(issueId) {
    try {
      const endpoint = `/organizations/${this.config.organization}/issues/${issueId}/`;
      const issue = await this.makeRequest(endpoint);
      
      return {
        id: issue.id,
        title: issue.title,
        level: issue.level,
        status: issue.status,
        count: issue.count,
        userCount: issue.userCount,
        firstSeen: issue.firstSeen,
        lastSeen: issue.lastSeen,
        permalink: issue.permalink,
        metadata: issue.metadata
      };
    } catch (error) {
      console.error('Failed to fetch issue details:', error);
      return null;
    }
  }

  // Get event details
  async getEventDetails(eventId) {
    try {
      const endpoint = `/projects/${this.config.organization}/${this.config.project}/events/${eventId}/`;
      const event = await this.makeRequest(endpoint);
      
      return {
        id: event.id,
        message: event.message,
        level: event.level,
        timestamp: event.timestamp,
        user: event.user,
        tags: event.tags,
        context: event.context
      };
    } catch (error) {
      console.error('Failed to fetch event details:', error);
      return null;
    }
  }

  // Resolve an issue
  async resolveIssue(issueId) {
    try {
      const endpoint = `/organizations/${this.config.organization}/issues/${issueId}/`;
      await fetch(`${this.config.baseUrl}${endpoint}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${this.config.token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          status: 'resolved'
        })
      });
      
      console.log(`Issue ${issueId} resolved`);
      return true;
    } catch (error) {
      console.error('Failed to resolve issue:', error);
      return false;
    }
  }

  // Ignore an issue
  async ignoreIssue(issueId) {
    try {
      const endpoint = `/organizations/${this.config.organization}/issues/${issueId}/`;
      await fetch(`${this.config.baseUrl}${endpoint}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${this.config.token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          status: 'ignored'
        })
      });
      
      console.log(`Issue ${issueId} ignored`);
      return true;
    } catch (error) {
      console.error('Failed to ignore issue:', error);
      return false;
    }
  }
}

const sentryService = new SentryService();
export default sentryService; 